/**
 * AGROMX Frontend - Role Permisos Matriz View
 * Matriz de permisos para configurar qué puede hacer un rol por módulo.
 */

import roleService from '../services/role.service.js';
import toast from '../utils/toast.js';

/**
 * Dependencias entre módulos (hardcoded — versión piloto).
 *
 * Si un rol tiene `triggerAcciones` del módulo origen, se sugiere también
 * activar la `accion` recomendada en el módulo destino.
 *
 * El admin SIEMPRE puede ignorar la sugerencia (el sistema solo avisa,
 * no fuerza). Esto sirve para evitar configuraciones donde un módulo
 * dispara efectos en otro y el rol no tiene visibilidad.
 */
const MODULO_DEPENDENCIAS = [
  {
    modulo: 'recepciones',
    triggerAcciones: ['crear', 'cerrar'],
    requiere: { modulo: 'pnc', acciones: ['ver', 'editar'] },
    razon: 'Las recepciones visuales pueden crear automáticamente PNCs cuando el estado del tractor es "malo" o "regular". Sin acceso a PNC el usuario no podrá consultar ni completar la información del PNC (descripción, acción, responsable de producción, evidencia).'
  }
];

export default {
  data: {
    rolId: null,
    rol: null,
    catalogo: null,
    // Set de "modulo_id:accion_id" para los permisos activos del rol
    seleccionados: new Set(),
    loading: true,
    guardando: false
  },

  async render(params = {}) {
    this.data.rolId = parseInt(params.id);
    return `
      <div>
        <div class="card">
          <div class="card-header">
            <h2 class="card-title" id="matrizTitulo">Permisos del rol</h2>
            <button class="btn btn-secondary" id="btnCancelar">Volver a Lista</button>
          </div>

          <div id="matrizContainer">
            <div style="text-align: center; padding: var(--spacing-xl);">
              <div class="loading-spinner" style="margin: 0 auto;"></div>
              <p>Cargando matriz de permisos...</p>
            </div>
          </div>

          <div style="display: flex; gap: 1rem; justify-content: flex-end; flex-wrap: wrap; margin-top: 1rem;">
            <button class="btn btn-primary" id="btnGuardar" disabled>Guardar Cambios</button>
          </div>
        </div>
      </div>
    `;
  },

  async afterRender() {
    window.matrizView = this;
    await this.cargarDatos();
    this.setupEventListeners();
  },

  async cargarDatos() {
    try {
      const [catalogoRes, rolRes] = await Promise.all([
        roleService.getCatalogo(),
        roleService.getById(this.data.rolId)
      ]);

      this.data.catalogo = catalogoRes;
      this.data.rol = rolRes.rol;

      // Convertir permisos actuales a Set para lookup rápido
      this.data.seleccionados = new Set(
        (this.data.rol.permisos || []).map(p => `${p.modulo_id}:${p.accion_id}`)
      );

      this.renderMatriz();
    } catch (err) {
      console.error('Error cargando matriz:', err);
      document.getElementById('matrizContainer').innerHTML = `
        <div class="alert alert-danger">No se pudo cargar la matriz de permisos.</div>
      `;
    } finally {
      this.data.loading = false;
    }
  },

  renderMatriz() {
    const { rol, catalogo } = this.data;

    document.getElementById('matrizTitulo').textContent = `Permisos: ${rol.nombre}`;

    if (rol.es_sistema) {
      document.getElementById('btnGuardar').style.display = 'none';
    }

    const container = document.getElementById('matrizContainer');
    if (!catalogo?.grupos?.length) {
      container.innerHTML = '<div class="alert alert-info">No hay módulos configurados.</div>';
      return;
    }

    const accionesMap = new Map(catalogo.acciones.map(a => [a.id, a]));
    const disabled = rol.es_sistema ? 'disabled' : '';

    // Módulos ocultos en la matriz (dormidos en BD pero no editables desde la UI)
    const MODULOS_OCULTOS = new Set(['personas']);

    const html = catalogo.grupos.map(grupo => {
      const modulosVisibles = (grupo.modulos || []).filter(m => !MODULOS_OCULTOS.has(m.clave));
      if (!modulosVisibles.length) return '';

      const modulosHtml = modulosVisibles.map(modulo => {
        const acciones = (modulo.acciones_ids || [])
          .map(id => accionesMap.get(id))
          .filter(Boolean)
          .sort((a, b) => a.orden - b.orden);

        const permisosActivos = acciones.filter(a =>
          this.data.seleccionados.has(`${modulo.id}:${a.id}`)
        ).length;

        const labelPermisos = permisosActivos > 0
          ? `<span class="perm-count-badge badge badge-success">${permisosActivos} permiso${permisosActivos > 1 ? 's' : ''}</span>`
          : `<span class="perm-count-badge badge badge-warning">Sin permisos</span>`;

        const accionesHtml = acciones.map(accion => {
          const key = `${modulo.id}:${accion.id}`;
          const checked = this.data.seleccionados.has(key);
          const colorClass = checked ? this.getAccionColor(accion.clave) : 'perm-tag-off';
          return `
            <button type="button" class="perm-tag ${colorClass}"
              data-modulo-id="${modulo.id}"
              data-accion-id="${accion.id}"
              data-checked="${checked ? '1' : '0'}"
              ${disabled}
            >
              <span class="perm-tag-dot"></span>
              ${this.escapeHtml(accion.nombre)}
            </button>
          `;
        }).join('');

        const todosActivos = permisosActivos === acciones.length;

        return `
          <div class="perm-modulo" data-modulo-id="${modulo.id}">
            <div class="perm-modulo-header" data-modulo-id="${modulo.id}" onclick="window.matrizView.toggleModulo(${modulo.id})">
              <div>
                <strong>${this.escapeHtml(modulo.nombre)}</strong>
              </div>
              <div style="display: flex; align-items: center; gap: 0.5rem;">
                ${labelPermisos}
                <button type="button" class="perm-expand" id="chevron-${modulo.id}">&#9660;</button>
              </div>
            </div>
            <div class="perm-modulo-body" id="body-${modulo.id}" style="display: none;">
              <div class="perm-tags-container">
                ${accionesHtml}
              </div>
              <div class="perm-toggle-all">
                <span>Activar todos</span>
                <label class="perm-switch">
                  <input type="checkbox" ${todosActivos ? 'checked' : ''} ${disabled}
                    onchange="window.matrizView.toggleTodos(${modulo.id}, this.checked)"
                  />
                  <span class="perm-switch-slider"></span>
                </label>
              </div>
            </div>
          </div>
        `;
      }).join('');

      return `
        <div class="perm-grupo">
          <div class="perm-grupo-header">
            <span>${this.escapeHtml(grupo.nombre).toUpperCase()}</span>
            <a href="javascript:void(0)" onclick="window.matrizView.expandirGrupo(${grupo.id})" class="perm-expand-link">Expandir todo</a>
          </div>
          <div class="perm-grupo-body" data-grupo-id="${grupo.id}">
            ${modulosHtml}
          </div>
        </div>
      `;
    }).join('');

    container.innerHTML = `
      <style>
        .perm-grupo { margin-bottom: 1.5rem; }
        .perm-grupo-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.5rem 0;
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--primary);
          letter-spacing: 0.05em;
        }
        .perm-expand {
          display: inline-block !important;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 0.85rem;
          color: var(--gray-600);
          padding: 0 var(--spacing-xs);
        }
        .perm-expand-link { color: var(--primary); font-weight: 500; font-size: 0.8rem; text-decoration: none; }
        .perm-expand-link:hover { text-decoration: underline; }
        .perm-grupo-body {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          overflow: hidden;
        }
        .perm-modulo { border-bottom: 1px solid #f3f4f6; }
        .perm-modulo:last-child { border-bottom: none; }
        .perm-modulo-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem 1rem;
          cursor: pointer;
        }
        .perm-modulo-header:hover { background: #f9fafb; }
        .perm-modulo-body {
          padding: 0.75rem 1rem 1rem;
          border-top: 1px solid #f3f4f6;
          background: #fafbfc;
        }
        .perm-tags-container { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 0.75rem; }
        .perm-tag {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          padding: 0.35rem 0.75rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          border: 1.5px solid transparent;
          transition: all 0.15s ease;
          user-select: none;
          background: #f3f4f6;
          color: var(--gray-500);
        }
        .perm-tag:hover { opacity: 0.85; }
        .perm-tag:disabled { opacity: 0.5; cursor: not-allowed; }
        .perm-tag-dot {
          width: 8px; height: 8px;
          border-radius: 50%;
          background: var(--gray-400);
        }
        .perm-tag-off { background: #f3f4f6; color: var(--gray-500); border-color: #e5e7eb; }
        .perm-tag-off .perm-tag-dot { background: var(--gray-300); }
        .perm-tag-primary { background: #dbeafe; color: var(--primary); border-color: var(--primary); }
        .perm-tag-primary .perm-tag-dot { background: var(--primary); }
        .perm-tag-success { background: #d1fae5; color: var(--success); border-color: var(--success); }
        .perm-tag-success .perm-tag-dot { background: var(--success); }
        .perm-tag-warning { background: #fef3c7; color: #b45309; border-color: var(--warning); }
        .perm-tag-warning .perm-tag-dot { background: var(--warning); }
        .perm-tag-danger { background: #fee2e2; color: var(--danger); border-color: var(--danger); }
        .perm-tag-danger .perm-tag-dot { background: var(--danger); }
        .perm-toggle-all {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 0.5rem;
          border-top: 1px solid #e5e7eb;
          font-size: 0.85rem;
          color: var(--gray-500);
        }
        .perm-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
        .perm-switch input { opacity: 0; width: 0; height: 0; }
        .perm-switch-slider {
          position: absolute; cursor: pointer;
          top: 0; left: 0; right: 0; bottom: 0;
          background: #cbd5e1;
          border-radius: 24px;
          transition: 0.2s;
        }
        .perm-switch-slider::before {
          content: "";
          position: absolute;
          height: 18px; width: 18px;
          left: 3px; bottom: 3px;
          background: white;
          border-radius: 50%;
          transition: 0.2s;
        }
        .perm-switch input:checked + .perm-switch-slider { background: var(--primary); }
        .perm-switch input:checked + .perm-switch-slider::before { transform: translateX(20px); }
        .perm-switch input:disabled + .perm-switch-slider { opacity: 0.5; cursor: not-allowed; }
        @media (max-width: 768px) {
          .perm-modulo-header { flex-direction: column; align-items: flex-start; gap: 0.25rem; }
        }
      </style>
      ${html}
    `;

    // Click en tags para toggle
    container.querySelectorAll('.perm-tag').forEach(tag => {
      tag.addEventListener('click', (e) => this.toggleTag(e.currentTarget));
    });
  },

  getAccionColor(clave) {
    const colores = {
      ver: 'perm-tag-primary',
      crear: 'perm-tag-success',
      editar: 'perm-tag-warning',
      eliminar: 'perm-tag-danger',
      aprobar: 'perm-tag-success',
      cerrar: 'perm-tag-warning',
      imprimir: 'perm-tag-primary',
      exportar: 'perm-tag-primary',
      asignar: 'perm-tag-success'
    };
    return colores[clave] || 'perm-tag-primary';
  },

  toggleModulo(moduloId) {
    const body = document.getElementById(`body-${moduloId}`);
    const chevron = document.getElementById(`chevron-${moduloId}`);
    if (!body) return;
    const visible = body.style.display !== 'none';
    body.style.display = visible ? 'none' : 'block';
    if (chevron) chevron.innerHTML = visible ? '&#9660;' : '&#9650;';
  },

  expandirGrupo(grupoId) {
    const grupoBody = document.querySelector(`[data-grupo-id="${grupoId}"]`);
    if (!grupoBody) return;
    const modulos = grupoBody.querySelectorAll('.perm-modulo');
    const allOpen = Array.from(modulos).every(m => {
      const id = m.dataset.moduloId;
      return document.getElementById(`body-${id}`)?.style.display !== 'none';
    });

    modulos.forEach(m => {
      const id = m.dataset.moduloId;
      const body = document.getElementById(`body-${id}`);
      const chevron = document.getElementById(`chevron-${id}`);
      if (body) body.style.display = allOpen ? 'none' : 'block';
      if (chevron) chevron.innerHTML = allOpen ? '&#9660;' : '&#9650;';
    });

    // Actualizar link text
    const link = grupoBody.parentElement.querySelector('.perm-expand-link');
    if (link) link.textContent = allOpen ? 'Expandir todo' : 'Colapsar todo';
  },

  toggleTag(tag) {
    if (tag.disabled) return;
    const moduloId = tag.dataset.moduloId;
    const accionId = tag.dataset.accionId;
    const key = `${moduloId}:${accionId}`;
    const isChecked = tag.dataset.checked === '1';
    const accionClave = this.data.catalogo.acciones.find(a => a.id === Number(accionId))?.clave;

    if (isChecked) {
      // Desmarcar
      if (accionClave === 'ver') {
        // Si desmarca "ver", desmarcar TODAS las acciones del módulo
        this.toggleTodos(Number(moduloId), false);
      } else {
        this.data.seleccionados.delete(key);
        tag.dataset.checked = '0';
        tag.className = 'perm-tag perm-tag-off';
        // Si desmarca "editar" y "crear" estaba marcado, desmarcar "crear"
        if (accionClave === 'editar') {
          this.autoDesmarcarAccion(moduloId, 'crear');
        }
      }
    } else {
      // Marcar
      this.data.seleccionados.add(key);
      tag.dataset.checked = '1';
      tag.className = `perm-tag ${this.getAccionColor(accionClave)}`;

      // Si marca cualquier acción, auto-marcar "ver"
      if (accionClave !== 'ver') {
        this.autoMarcarVer(moduloId);
      }
      // Si marca "crear", auto-marcar "editar"
      if (accionClave === 'crear') {
        this.autoMarcarAccion(moduloId, 'editar');
      }
    }

    // Actualizar switch "Activar todos"
    this.updateToggleAll(moduloId);
    // Actualizar contador en header del módulo
    this.updateModuloCount(moduloId);

    document.getElementById('btnGuardar').disabled = false;
  },

  autoMarcarVer(moduloId) {
    const accionVer = this.data.catalogo.acciones.find(a => a.clave === 'ver');
    if (!accionVer) return;
    const key = `${moduloId}:${accionVer.id}`;
    if (this.data.seleccionados.has(key)) return; // ya marcado

    this.data.seleccionados.add(key);
    const tagVer = document.querySelector(`.perm-tag[data-modulo-id="${moduloId}"][data-accion-id="${accionVer.id}"]`);
    if (tagVer) {
      tagVer.dataset.checked = '1';
      tagVer.className = `perm-tag ${this.getAccionColor('ver')}`;
    }
  },

  autoDesmarcarAccion(moduloId, accionClave) {
    const accion = this.data.catalogo.acciones.find(a => a.clave === accionClave);
    if (!accion) return;
    const key = `${moduloId}:${accion.id}`;
    if (!this.data.seleccionados.has(key)) return;

    this.data.seleccionados.delete(key);
    const tag = document.querySelector(`.perm-tag[data-modulo-id="${moduloId}"][data-accion-id="${accion.id}"]`);
    if (tag) {
      tag.dataset.checked = '0';
      tag.className = 'perm-tag perm-tag-off';
    }
  },

  autoMarcarAccion(moduloId, accionClave) {
    const accion = this.data.catalogo.acciones.find(a => a.clave === accionClave);
    if (!accion) return;
    const key = `${moduloId}:${accion.id}`;
    if (this.data.seleccionados.has(key)) return;

    this.data.seleccionados.add(key);
    const tag = document.querySelector(`.perm-tag[data-modulo-id="${moduloId}"][data-accion-id="${accion.id}"]`);
    if (tag) {
      tag.dataset.checked = '1';
      tag.className = `perm-tag ${this.getAccionColor(accionClave)}`;
    }
  },

  toggleTodos(moduloId, checked) {
    const modulo = document.querySelector(`.perm-modulo[data-modulo-id="${moduloId}"]`);
    if (!modulo) return;

    modulo.querySelectorAll('.perm-tag').forEach(tag => {
      const key = `${tag.dataset.moduloId}:${tag.dataset.accionId}`;
      if (checked) {
        this.data.seleccionados.add(key);
        tag.dataset.checked = '1';
        const accionClave = this.data.catalogo.acciones.find(a => a.id === Number(tag.dataset.accionId))?.clave;
        tag.className = `perm-tag ${this.getAccionColor(accionClave)}`;
      } else {
        this.data.seleccionados.delete(key);
        tag.dataset.checked = '0';
        tag.className = 'perm-tag perm-tag-off';
      }
    });

    this.updateModuloCount(moduloId);
    document.getElementById('btnGuardar').disabled = false;
  },

  updateToggleAll(moduloId) {
    const modulo = document.querySelector(`.perm-modulo[data-modulo-id="${moduloId}"]`);
    if (!modulo) return;
    const tags = modulo.querySelectorAll('.perm-tag');
    const allChecked = Array.from(tags).every(t => t.dataset.checked === '1');
    const switchEl = modulo.querySelector('.perm-switch input');
    if (switchEl) switchEl.checked = allChecked;
  },

  updateModuloCount(moduloId) {
    const modulo = document.querySelector(`.perm-modulo[data-modulo-id="${moduloId}"]`);
    if (!modulo) return;
    const tags = modulo.querySelectorAll('.perm-tag');
    const count = Array.from(tags).filter(t => t.dataset.checked === '1').length;
    const badgeEl = modulo.querySelector('.perm-count-badge');
    if (!badgeEl) return;
    if (count > 0) {
      badgeEl.className = 'perm-count-badge badge badge-success';
      badgeEl.textContent = `${count} permiso${count > 1 ? 's' : ''}`;
    } else {
      badgeEl.className = 'perm-count-badge badge badge-warning';
      badgeEl.textContent = 'Sin permisos';
    }
  },

  setupEventListeners() {
    document.getElementById('btnCancelar')?.addEventListener('click', () => {
      window.location.href = '/admin/roles';
    });

    document.getElementById('btnGuardar')?.addEventListener('click', () => {
      this.guardar();
    });
  },

  /**
   * Detecta dependencias incumplidas entre módulos.
   * Retorna array con las violaciones encontradas.
   */
  _validarDependencias() {
    const violaciones = [];
    const cat = this.data.catalogo;
    if (!cat) return violaciones;

    // Construir índice clave→{moduloId, accionId} desde el catálogo
    const moduloPorClave = new Map();
    for (const grupo of (cat.grupos || [])) {
      for (const m of (grupo.modulos || [])) {
        moduloPorClave.set(m.clave, m);
      }
    }
    const accionPorClave = new Map();
    for (const a of (cat.acciones || [])) {
      accionPorClave.set(a.clave, a);
    }

    // Helper: ¿el rol tiene esta combinación módulo:acción seleccionada?
    const tienePermiso = (moduloClave, accionClave) => {
      const m = moduloPorClave.get(moduloClave);
      const a = accionPorClave.get(accionClave);
      if (!m || !a) return false;
      return this.data.seleccionados.has(`${m.id}:${a.id}`);
    };

    for (const dep of MODULO_DEPENDENCIAS) {
      // ¿Tiene alguna acción que dispare la dependencia?
      const tieneTrigger = dep.triggerAcciones.some(acc => tienePermiso(dep.modulo, acc));
      if (!tieneTrigger) continue;

      // Soporta tanto `accion` (string) como `acciones` (array)
      const accionesRequeridas = dep.requiere.acciones || [dep.requiere.accion];

      // ¿Le falta alguna de las acciones requeridas?
      const faltantes = accionesRequeridas.filter(acc => !tienePermiso(dep.requiere.modulo, acc));
      if (faltantes.length > 0) {
        violaciones.push({ ...dep, accionesFaltantes: faltantes });
      }
    }

    return violaciones;
  },

  /**
   * Activa todos los permisos requeridos por las dependencias.
   * Mutation directo del Set seleccionados.
   */
  _activarDependenciasFaltantes(violaciones) {
    const cat = this.data.catalogo;
    if (!cat) return;

    const moduloPorClave = new Map();
    for (const grupo of (cat.grupos || [])) {
      for (const m of (grupo.modulos || [])) {
        moduloPorClave.set(m.clave, m);
      }
    }
    const accionPorClave = new Map();
    for (const a of (cat.acciones || [])) {
      accionPorClave.set(a.clave, a);
    }

    for (const v of violaciones) {
      const m = moduloPorClave.get(v.requiere.modulo);
      if (!m) continue;
      // Activa solo las acciones que faltan (no las que ya tiene)
      const acciones = v.accionesFaltantes || v.requiere.acciones || [v.requiere.accion];
      for (const accClave of acciones) {
        const a = accionPorClave.get(accClave);
        if (a) this.data.seleccionados.add(`${m.id}:${a.id}`);
      }
    }
  },

  async guardar() {
    if (this.data.guardando) return;

    // Validar dependencias antes de enviar
    const violaciones = this._validarDependencias();
    if (violaciones.length > 0) {
      const decision = await this._confirmarDependencias(violaciones);
      if (decision === 'cancelar') {
        return; // No hace nada, el admin puede revisar
      }
      if (decision === 'activar') {
        this._activarDependenciasFaltantes(violaciones);
        // Re-render visual de la matriz para reflejar los cambios
        this._reRenderMatriz();
      }
      // Si decision === 'ignorar', sigue con el guardado tal cual
    }

    this.data.guardando = true;
    const btnGuardar = document.getElementById('btnGuardar');
    btnGuardar.disabled = true;
    btnGuardar.textContent = 'Guardando...';

    const permisos = Array.from(this.data.seleccionados).map(key => {
      const [modulo_id, accion_id] = key.split(':').map(Number);
      return { modulo_id, accion_id };
    });

    try {
      await roleService.updatePermisos(this.data.rolId, permisos);
      btnGuardar.textContent = 'Guardar cambios';
      toast.show('Permisos guardados exitosamente', 'success');
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err.message || 'Error al guardar';
      toast.show(msg, 'error');
      btnGuardar.disabled = false;
      btnGuardar.textContent = 'Guardar cambios';
    } finally {
      this.data.guardando = false;
    }
  },

  /**
   * Muestra un modal con las dependencias incumplidas y devuelve la decisión:
   * 'activar' | 'ignorar' | 'cancelar'
   */
  _confirmarDependencias(violaciones) {
    return new Promise((resolve) => {
      // Construir lista compacta
      const items = violaciones.map(v => {
        const acciones = (v.accionesFaltantes || v.requiere.acciones || [v.requiere.accion]).join(', ');
        return `<li style="margin-bottom: 0.25rem;"><strong>${this._modNombre(v.modulo)}</strong> requiere <strong>${this._modNombre(v.requiere.modulo)}</strong> (${acciones})</li>`;
      }).join('');

      const html = `
        <div style="padding: 0.25rem 0; font-size: 0.9rem;">
          <ul style="margin: 0 0 0.5rem; padding-left: 1.25rem; color: var(--gray-700);">${items}</ul>
          <p style="margin: 0; font-size: 0.8rem; color: var(--gray-500);">¿Activar también las dependencias?</p>
        </div>
      `;

      // Usar modal global si está disponible, sino fallback a confirm
      if (window.modal && typeof window.modal.open === 'function') {
        window.modal.open(
          '⚠️ Dependencias detectadas',
          html,
          [
            { text: 'Activar', class: 'btn-primary', onClick: () => { window.modal.close(); resolve('activar'); } },
            { text: 'Guardar como está', class: 'btn-secondary', onClick: () => { window.modal.close(); resolve('ignorar'); } },
            { text: 'Cancelar', class: 'btn-text', onClick: () => { window.modal.close(); resolve('cancelar'); } }
          ],
          { dismissable: false }
        );
      } else {
        // Fallback: confirm nativo (menos elegante pero funcional)
        const txt = violaciones.map(v => {
          const acciones = (v.accionesFaltantes || v.requiere.acciones || [v.requiere.accion]).join(', ');
          return `• ${this._modNombre(v.modulo)} → ${this._modNombre(v.requiere.modulo)} (${acciones}): ${v.razon}`;
        }).join('\n\n');
        const ok = window.confirm(
          `Dependencias detectadas:\n\n${txt}\n\n¿Activar también las dependencias recomendadas?\n\nAceptar = Activar | Cancelar = Guardar como está`
        );
        resolve(ok ? 'activar' : 'ignorar');
      }
    });
  },

  /** Helper: obtiene el nombre legible de un módulo por clave */
  _modNombre(clave) {
    const cat = this.data.catalogo;
    if (!cat) return clave;
    for (const g of (cat.grupos || [])) {
      for (const m of (g.modulos || [])) {
        if (m.clave === clave) return m.nombre;
      }
    }
    return clave;
  },

  /** Re-renderiza la matriz para reflejar cambios visuales después de auto-activar */
  _reRenderMatriz() {
    // renderMatriz modifica el DOM directamente, no devuelve HTML
    this.renderMatriz();
  },

  escapeHtml(str) {
    if (str === null || str === undefined) return '';
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
};
